import { SchemaType } from "./Schema";

/**
 * The following are the parts of a given 'resourceId' string:
 *
 *      e.g. resourceId = "propertySets:autodesk.parameter.aec.enum:assets.design-6.0.0-beta.3"
 *
 *      | Resource ID                                                                               |
 *      | Schema Type  | Resource Name                                                              |
 *      | Extension ID                                                 | Version                    |
 *      | Entity ID                                    | Schema Name   | Version Core | Pre-Release |
 *      | Schema Type  | Qualifier                     | Schema Name   | Version Core | Pre-Release |
 *      | Schema Type  | Company  | Namespace          | Schema Name   | Version Core | Pre-Release |
 *      | ------------ | ----------------------------- | ------------- | -------------------------- |
 *      | propertySets | autodesk | parameter.aec.enum | assets.design |     6.0.0    |    beta.3   |
 *
 *
 * The following breakdown illustrates the parts of a 'resourceId':
 *
 *      resourceId = propertySets:autodesk.parameter.aec.enum:assets.design-6.0.0-beta.3
 *
 *      resourceId = extensionId + version
 *                 = ["propertySets:autodesk.parameter.aec.enum:assets.design", "6.0.0-beta.3"]
 *
 *      resourceId = extensionId + versionCore + PreRelease
 *                 = ["propertySets:autodesk.parameter.aec.enum:assets.design", "6.0.0", "beta.3"]
 *
 *      resourceId = entityId + schemaName + versionCore + PreRelease
 *                 = ["propertySets:autodesk.parameter.aec.enum", "assets.design", "6.0.0", "beta.3"]
 *
 *      resourceId = schemaType + qualifier + schemaName + versionCore + PreRelease
 *                 = ["propertySets", "autodesk.parameter.aec.enum", "assets.design", "6.0.0", "beta.3"]
 *
 *      resourceId = schemaType + company + namespace + schemaName + versionCore + PreRelease
 *                 = ["propertySets", "autodesk", "parameter.aec.enum", "assets.design", "6.0.0", "beta.3"]
 *
 *      resourceId = schemaType + resourceName
 *                 = ["propertySets", "autodesk.parameter.aec.enum:assets.design-6.0.0-beta.3"]
 *
 * Note that first part of 'schemaName' is referred to as 'categoryName'.
 * For example, the 'categoryName' of schema name 'assets.design' is 'assets'
 */

export class ExtensionId {
    private readonly _extensionId: string;
    private _schemaType: SchemaType;
    private readonly _qualifier: string;
    private readonly _company: string;
    private readonly _namespace: string;
    private readonly _schemaName: string;
    private readonly _categoryName: string;

    constructor(extensionId: string) {
        // {schemaType}:autodesk.parameter.aec.enum:assets.design (generic schema types)
        // json:autodesk.parameter.aec.enum:assets.design (for JSON schemas)
        // adsk:autodesk.parameter.aec.enum:assets.design (for JSON schemas - legacy)
        // adsk:autodesk.parameter.aec.enum_assets.design (for adsk-json schemas)
        // adsk-json:autodesk.parameter.aec.enum_assets.design (for adsk-json schemas)
        const parts = extensionId.split(":").filter((p) => !!p);

        const invalid = (): never => {
            throw `Invalid extension ID: '${extensionId}'`;
        };

        const parseQualifierAndSchemaNameFromUnderscore = (
            value: string,
            options?: { allowVersionSuffix?: boolean }
        ): { qualifier: string; schemaName: string } => {
            const underscoreIndex = value.indexOf("_");
            if (underscoreIndex <= 0 || underscoreIndex >= value.length - 1) {
                invalid();
            }

            const allowVersionSuffix = options?.allowVersionSuffix ?? false;
            let withoutVersion = value;
            if (allowVersionSuffix) {
                const dashIndex = value.indexOf("-", underscoreIndex + 1);
                if (dashIndex >= 0) {
                    withoutVersion = value.slice(0, dashIndex);
                }
            }

            // Note: underscoreIndex still points to the same `_` because we only strip after it.
            return {
                qualifier: withoutVersion.slice(0, underscoreIndex),
                schemaName: withoutVersion.slice(underscoreIndex + 1),
            };
        };

        // These are assigned in the parse branches below; `invalid()` always throws.
        let schemaType!: SchemaType;
        let qualifier!: string;
        let schemaName!: string;

        const prefix = parts[0];
        if (prefix === "adsk") {
            // adsk:{qualifier}:{schemaName}   -> SchemaType.json (legacy JSON)
            // adsk:{qualifier}_{schemaName}  -> SchemaType.adskJson (adsk-json drafts/templates)
            if (parts.length === 3) {
                schemaType = SchemaType.json;
                qualifier = parts[1];
                schemaName = parts[2];
            } else if (parts.length === 2) {
                schemaType = SchemaType.adskJson;
                ({ qualifier, schemaName } = parseQualifierAndSchemaNameFromUnderscore(parts[1]));
            } else {
                invalid();
            }
        } else if (prefix === SchemaType.adskJson) {
            // adsk-json:{qualifier}_{schemaName}[-{version}]  (we tolerate the accidental version suffix)
            if (parts.length === 2) {
                schemaType = SchemaType.adskJson;
                ({ qualifier, schemaName } = parseQualifierAndSchemaNameFromUnderscore(parts[1], {
                    allowVersionSuffix: true,
                }));
            } else if (parts.length === 3) {
                // Backwards-compatible fallback (rare): adsk-json:{qualifier}:{schemaName}
                schemaType = SchemaType.adskJson;
                qualifier = parts[1];
                schemaName = parts[2];
            } else {
                invalid();
            }
        } else {
            // Default: {schemaType}:{qualifier}:{schemaName}
            if (parts.length !== 3) {
                invalid();
            }
            schemaType = prefix as SchemaType;
            qualifier = parts[1];
            schemaName = parts[2];
        }

        this._schemaType = schemaType;
        this._qualifier = qualifier;
        this._schemaName = schemaName;
        this._extensionId = extensionId;

        const firstPeriodIndex = this._qualifier.indexOf(".");
        if (firstPeriodIndex < 0) {
            throw `Invalid qualifier: '${this._qualifier}'`;
        }

        this._company = this._qualifier.substring(0, firstPeriodIndex); // autodesk
        this._namespace = this._qualifier.substring(firstPeriodIndex + 1); // parameter.aec.enum

        const nameParts = this._schemaName.split(".");
        this._categoryName = nameParts[0]; // assets
    }

    static parse(extensionId: string): ExtensionId | undefined {
        try {
            return new ExtensionId(extensionId);
        } catch (error) {
            return undefined;
        }
    }

    public get schemaType(): SchemaType {
        return this._schemaType;
    }

    public set schemaType(schemaType: SchemaType) {
        this._schemaType = schemaType;
    }

    public get qualifier(): string {
        return this._qualifier;
    }

    public get company(): string {
        return this._company;
    }

    public get namespace(): string {
        return this._namespace;
    }

    public get schemaName(): string {
        return this._schemaName;
    }

    public get categoryName(): string {
        return this._categoryName;
    }

    public get extensionId(): string {
        return this._extensionId;
    }
}

export class ResourceId extends ExtensionId {
    private readonly _resourceId: string;
    private readonly _version: string;
    private readonly _versionCore: string;
    private readonly _preRelease: string;

    constructor(resourceId: string) {
        // `resourceId` format:
        //   {extensionId}-{versionCore}[-{preRelease}]
        //
        // IMPORTANT: `{extensionId}` can contain `-` (e.g. schemaType `adsk-json`),
        // so we must NOT split the whole string by `-` from the left.
        //
        // The version separator `-` is the first `-` AFTER the last `:` (i.e. after schemaName begins).
        const lastColonIndex = resourceId.lastIndexOf(":");
        const dashIndex = lastColonIndex >= 0 ? resourceId.indexOf("-", lastColonIndex + 1) : -1;

        const extensionId = dashIndex >= 0 ? resourceId.slice(0, dashIndex) : resourceId;
        super(extensionId);

        // Must be "{extensionId}-{versionCore}" or "{extensionId}-{versionCore}-{preRelease}"
        if (dashIndex < 0) {
            throw `Invalid resource ID: '${resourceId}'`;
        }

        const versionPart = resourceId.slice(dashIndex + 1);
        if (!versionPart) {
            throw `Invalid resource ID: '${resourceId}'`;
        }

        const [versionCore, ...preReleaseParts] = versionPart.split("-").filter((p) => p.length > 0);
        if (!versionCore) {
            throw `Invalid resource ID: '${resourceId}'`;
        }

        this._versionCore = versionCore;
        this._preRelease = preReleaseParts.join("-");

        // Preserve the original behavior: version includes pre-release when present.
        this._version = versionPart;
        this._resourceId = resourceId;
    }

    static parse(resourceId: string): ResourceId | undefined {
        try {
            return new ResourceId(resourceId);
        } catch (error) {
            return undefined;
        }
    }

    public get version(): string {
        return this._version;
    }

    public get versionCore(): string {
        return this._versionCore;
    }

    public get preRelease(): string {
        return this._preRelease;
    }

    public get resourceId(): string {
        return this._resourceId;
    }

    public get resourceName(): string {
        const firstColonIndex = this._resourceId.indexOf(":");
        return this._resourceId.substring(firstColonIndex + 1);
    }
}
